import React, { useCallback, useState } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { LoadableLazy } from '@confluence/loadable';

import type { GuardRedactionFlagPlugin } from './guardRedactionFlagPluginType.ts';
import { createPlugin, type GuardRedactionFlagPluginState } from './pm-plugins/main.ts';
import { guardRedactionFlagPluginKey } from './pm-plugins/pluginKey.ts';

const LazyGuardRedactionFlagContent = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-guard-redaction-flag-content" */ './ui/GuardRedactionFlagContent.tsx'
			)
		).GuardRedactionFlagContent,
});

type MountProps = {
	api: ExtractInjectionAPI<GuardRedactionFlagPlugin> | undefined;
	contentId: string | undefined;
	editorView: EditorView;
	flags: FlagsStateContainer | undefined;
};

// Most sessions never see a redaction, so the flag chunk is only fetched (and
// its load hold only taken) once the first one arrives.
const GuardRedactionFlagMount = (props: MountProps) => {
	const [hasRedactions, setHasRedactions] = useState(false);

	const onPluginState = useCallback(
		({
			guardRedactionFlagState,
		}: {
			guardRedactionFlagState: GuardRedactionFlagPluginState | undefined;
		}) => {
			if ((guardRedactionFlagState?.insertedTotal ?? 0) > 0) {
				setHasRedactions(true);
			}
		},
		[],
	);
	usePluginStateEffect(props.api, ['guardRedactionFlag'], onPluginState);

	return hasRedactions ? <LazyGuardRedactionFlagContent {...props} /> : null;
};

export const guardRedactionFlagPlugin: GuardRedactionFlagPlugin = ({ api, config }) => ({
	name: 'guardRedactionFlag',
	pmPlugins: () => [{ name: 'guardRedactionFlagPmPlugin', plugin: createPlugin }],
	getSharedState: (state) => (state ? guardRedactionFlagPluginKey.getState(state) : undefined),
	contentComponent: ({ editorView }) =>
		editorView ? (
			<GuardRedactionFlagMount
				api={api}
				contentId={config.contentId}
				editorView={editorView}
				flags={config.flags}
			/>
		) : null,
});
